import type {
  DisputeBadgeType,
  DisputeRow,
} from "@features/vessel-call/vessel-call-status/Operations/types/types";
import type {
  Dispute,
  DisputeStatus,
  DisputeType,
  DisputeUpdateAction,
} from "@services/disputes/types";
import type { TFunction } from "i18next";

const LANG_BASE = "vessel-call.operations";

export const formatAssociatedMovement = (movement?: string | null) =>
  movement
    ? movement
        .split("->")
        .map((part) => part.trim())
        .join(" → ")
    : "-";

export const DISPUTE_TYPE = {
  DISPUTE: "DISPUTE",
  NO_DISPUTE: "NO_DISPUTE",
} as const satisfies Record<string, DisputeType>;

export const DISPUTE_STATUSES = {
  DISPUTE_RAISED: "DISPUTE_RAISED",
  NO_DISPUTE: "NO_DISPUTE",
  RESOLUTION_PENDING: "RESOLUTION_PENDING",
  RESOLVED: "RESOLVED",
} as const satisfies Record<string, DisputeStatus>;

export const DISPUTE_ACTIONS = {
  ACCEPT: "ACCEPT",
  REJECT: "REJECT",
  NO_DISPUTE: "NO_DISPUTE",
} as const satisfies Record<string, DisputeUpdateAction>;

export const getDisputeStatusBadgeType = (status?: string): DisputeBadgeType => {
  if (status === DISPUTE_STATUSES.NO_DISPUTE || status === DISPUTE_STATUSES.RESOLVED)
    return "success";

  if (status === DISPUTE_STATUSES.RESOLUTION_PENDING) return "warning";
  return "error";
};

export const mapDisputeStatus = (
  status: DisputeStatus,
  t: TFunction,
): { disputeStatus: string; badgeType: DisputeBadgeType } => ({
  disputeStatus: t(`${LANG_BASE}.status-labels.${status}`),
  badgeType: getDisputeStatusBadgeType(status),
});

export const mapDisputeToRow = (dispute: Dispute, t: TFunction): DisputeRow => {
  const statusInfo = mapDisputeStatus(dispute.status, t);
  const currentHistoryType =
    dispute.status === DISPUTE_STATUSES.NO_DISPUTE ? DISPUTE_TYPE.NO_DISPUTE : DISPUTE_TYPE.DISPUTE;
  const currentHistoryEntry = [...(dispute.history ?? [])]
    .reverse()
    .find(
      (historyEntry) =>
        historyEntry.type === currentHistoryType ||
        (currentHistoryType === DISPUTE_TYPE.DISPUTE &&
          historyEntry.type === DISPUTE_STATUSES.DISPUTE_RAISED),
    );

  return {
    id: dispute.id,
    disputeRequestNumber: dispute.disputeRequestNumber,
    terminal: dispute.terminal,
    disputeReason: currentHistoryEntry?.disputeDetails ?? dispute.disputeDetails,
    submittedBy: currentHistoryEntry?.createdBy ?? dispute.createdBy,
    submissionDate: currentHistoryEntry?.createdAt ?? dispute.createdAt,
    ...statusInfo,
    associatedMovement: formatAssociatedMovement(dispute.associatedMovement),
  };
};
